/**
 * The names a PO-line change-log row can carry for one field.
 *
 * DATE-FIELD-CANON PR 2 (15 Sep 2026): the Customer Required Date column was
 * renamed `delivery_date` -> `customer_required_date`. The backend writes the
 * model name into `POChangeLog.field_changed`; rows written before the rename
 * carry the old one. Every reader of `field_changed` that counts, groups or
 * labels it resolves through here so the two names are ONE field on screen
 * (badge counts, pending-change cards, change-request grouping, audit and
 * changelog labels); the legacy change-history view reads the change-request
 * helper maps, which carry both names.
 */

import {
  CUSTOMER_REQUIRED_DATE,
  MRP_SCHEDULED_DATE,
  ORIGINAL_REQUIRED_DATE,
  SUPPLIER_PROMISE_DATE,
} from '../../config/dateLabels';

/** Every `field_changed` value that means the Customer Required Date. */
export const CRD_FIELD_NAMES: readonly string[] = ['customer_required_date', 'delivery_date'];

/**
 * Collapse a change-log field name onto its canonical (model) name.
 *
 * @param field - `field_changed` as the API returned it
 * @returns `customer_required_date` for either CRD spelling; otherwise the input
 */
export function canonicalChangeField(field: string): string {
  return CRD_FIELD_NAMES.includes(field) ? 'customer_required_date' : field;
}

/**
 * Reason prefixes for rows that are NOT somebody proposing a change.
 *
 * Each is written by the backend and named on `POChangeLog`
 * (`IMPORT_REASON_PREFIX`, `DIRECT_SET_REASON_PREFIX`) — the row carries no
 * other flag that separates machine churn and admin corrections from the
 * expediting conversation, and the reason was the one place to put it without
 * a migration. Keep these two strings in step with the model's.
 */
export const NON_NEGOTIATED_REASON_PREFIXES: readonly string[] = [
  'PO import',
  'Set before acknowledgement',
];

/**
 * Was this row part of the expediting conversation, or bookkeeping?
 *
 * Aaron, 17 Sep 2026, on the per-field change counters in the PO line modal:
 * *"The count is just for the expediting flow. When the supplier or customer
 * proposes a new date on each one, it's not for that initial one where the
 * customer can just change the date on their side... that's more of an admin
 * thing."*
 */
export function isNegotiatedChange(reason?: string | null): boolean {
  const text = (reason || '').trimStart();
  return !NON_NEGOTIATED_REASON_PREFIXES.some((p) => text.startsWith(p));
}

const DATE_FIELD_LABELS: Record<string, string> = {
  customer_required_date: CUSTOMER_REQUIRED_DATE,
  original_required_date: ORIGINAL_REQUIRED_DATE,
  // Rows written before the ODD column was renamed (14 Sep 2026) carry this spelling.
  original_promise_date: ORIGINAL_REQUIRED_DATE,
  supplier_promise_date: SUPPLIER_PROMISE_DATE,
  mrp_scheduled_date: MRP_SCHEDULED_DATE,
  // The two STAGING slots. Their column names say "supplier" but either side
  // writes them — on the schedule grain the customer proposes into the same
  // field (ScheduleWorkflowService.propose_quantity_change /
  // propose_alternative_date). Title-casing the column therefore printed
  // "Supplier proposed qty" as the heading of a row whose own body read
  // "Customer proposed quantity change". The reason text already names the
  // raiser, so the heading states the field neutrally and stops contradicting it.
  supplier_proposed_qty: 'Proposed Qty',
  supplier_proposed_date: 'Proposed Date',
};

/**
 * Human label for a change-log field name: the canon label for the four PO-line
 * dates (both CRD spellings collapse to one), Title Case for everything else.
 *
 * @param field - `field_changed` as the API returned it
 * @returns the label a screen shows for that field
 */
export function changeFieldLabel(field: string): string {
  const canonical = canonicalChangeField(field);
  return DATE_FIELD_LABELS[canonical] ?? canonical
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** The fields of a change-log row that decide whether it counts as a change. */
export interface CountableChangeRow {
  field_changed: string;
  reason?: string | null;
  new_value?: string | null;
  change_request_ref?: number | null;
}

/**
 * How many CHANGES a field has been through — the number beside a field in the
 * PO line modal.
 *
 * Lifted out of `usePOLineDetail` so it can be tested as what it is, a pure
 * function over rows: both rules below are ones Aaron asked for by name on the
 * 17 Sep walkthrough and neither had a test anywhere (the test lens, gate round
 * 1), because reaching them meant rendering a hook that fetches on mount.
 *
 * The rules, in the order they apply:
 *
 * 1. NEGOTIATED ROWS ONLY. An ERP import row and the customer's direct date set
 *    on a `new` line are bookkeeping, not a conversation: *"the count is just
 *    for the expediting flow... that's more of an admin thing."*
 * 2. THE HOLD COUNTS ITS ON TRANSITIONS. Hold rows are a toggle, not a request:
 *    both directions are written and neither carries a ref, so hold -> release
 *    -> hold counted three. *"It should count how many times the line has been
 *    put on hold."*
 * 3. EVERYTHING ELSE COUNTS ITS REQUESTS. One change request writes a propose
 *    row and a decision row; post-CR-4865 they share a `change_request_ref`, so
 *    count distinct refs. Older rows have none, so a decision-looking reason is
 *    skipped to keep a propose/decision pair at one.
 *
 * @param rows - the line's change log, any order.
 * @param fieldName - the field asked about; both CRD spellings are one field.
 * @returns the count, or null when there is nothing to show.
 */
export function countChanges(
  rows: readonly CountableChangeRow[],
  fieldName: string,
): number | null {
  const isDecisionRow = (reason?: string | null) =>
    /\b(accepted|declined|rejected|cancelled)\b/i.test(reason || '');
  const seenRefs = new Set<number>();
  let count = 0;
  const wanted = canonicalChangeField(fieldName);
  rows.forEach((log) => {
    if (canonicalChangeField(log.field_changed) !== wanted) return;
    if (!isNegotiatedChange(log.reason)) return;
    if (wanted === 'on_hold') {
      if (log.new_value === 'Yes') count++;
      return;
    }
    if (log.change_request_ref != null) {
      if (!seenRefs.has(log.change_request_ref)) {
        seenRefs.add(log.change_request_ref);
        count++;
      }
    } else if (!isDecisionRow(log.reason)) {
      count++;
    }
  });
  return count > 0 ? count : null;
}

/** The two user objects a change-log row can carry (serializer `UserSerializer`). */
interface ChangeLogUsers {
  changed_by_details?: { full_name?: string } | null;
  acting_as_user_details?: { id?: number; full_name?: string; first_name?: string; last_name?: string; email?: string } | null;
}

/**
 * Who actually made this change.
 *
 * EXPEDITING-3 (Aaron, 30 Sep 2026): *"it should show the actual user that did
 * it"* -- a customer user logged in as a supplier, which is common, must be named
 * as themselves, not as the supplier user they logged in as. Legacy records it
 * that way: the USER is `LoginAsSuperId` when set, the COMPANY stays the company
 * logged in as (the house pattern, e.g. BLL_OpenOrders.cs:1199 and BLL_NCRs.cs:355).
 * Replaces CR-4713's "(on behalf of ...)" suffix. The server records the real
 * user only from an open impersonation session (round 1).
 *
 * @param log - The row's user details.
 * @returns The real user's name, else the recorded user's, else "System".
 */
export function changeActorName(log: ChangeLogUsers): string {
  const real = log.acting_as_user_details;
  if (real) {
    // Round 1 (type lens #13): a nameless real user is still the real user --
    // never fall back to the account they were logged in as.
    return real.full_name || [real.first_name, real.last_name].filter(Boolean).join(' ')
      || real.email || `User #${real.id ?? '?'}`;
  }
  return log.changed_by_details?.full_name || 'System';
}

/**
 * CR-8596: the row a propose writer opens — every propose path in
 * POLineWorkflowService starts its reason "Supplier proposed …" or
 * "Customer proposed …". A proposal is identified by that prefix and never by
 * the words inside it: a supplier's own reason ("will be rejected", "the
 * last one was accepted") is free text about their request, not a decision.
 * The Cost tab's price change requests keep the same rule on the backend
 * (purchasing/price_change_requests.py, _PROPOSAL).
 */
export const PROPOSAL_REASON_PATTERN = /^(supplier|customer) proposed /i;

/** Is this change-log row the proposal of a change request? */
export function isProposalRow(entry: { reason?: string | null }): boolean {
  return PROPOSAL_REASON_PATTERN.test((entry.reason || '').trim());
}

/** Opens the reason of the row "Close request" writes (po_line_close_request.CLOSED_REQUEST_REASON_PREFIX). */
export const CLOSED_REQUEST_REASON_PREFIX = 'Declined request closed';

/**
 * Is this the change-log row a "Close request" wrote? Keyed on the close's own
 * reason marker, not on the change_declined -> accepted pair (round 1: a
 * supplier's plain accept on a declined line makes that pair too, and moves a
 * date, so it must still read "Change Accepted").
 */
export function isClosedRequestRow(entry: { field_changed: string; reason?: string | null }): boolean {
  return entry.field_changed === 'acknowledge_status'
    && (entry.reason || '').trimStart().startsWith(CLOSED_REQUEST_REASON_PREFIX);
}

/** Where a closed request put the line: back to New for an unanswered order, else WIP. */
export function closedRequestOutcome(entry: { new_value?: string | null }): string {
  return entry.new_value === 'new' ? 'New' : 'WIP';
}

/**
 * The row a proposer's WITHDRAWAL wrote (`cancel_field_change`).
 *
 * The withdrawal puts the line back where it was — usually `accepted` — so its
 * new_value reads like an acceptance, and every surface that tested new_value
 * first called a withdrawn request "Accepted" (CR-7408 rework, tester 7 Oct).
 * The backend opens the reason with "<field> change cancelled by <side>." and
 * only appends the user's own text after it, so the opening is a marker a
 * typed reason cannot forge.
 */
const WITHDRAWAL_REASON = /^[a-z ]+ change cancelled by (supplier|customer)\./i;

export function isWithdrawalRow(entry: { field_changed: string; reason?: string | null }): boolean {
  return entry.field_changed === 'acknowledge_status'
    && WITHDRAWAL_REASON.test((entry.reason || '').trimStart());
}
